/**
 * [BH-1 / BH-2 / BH-3 2026-09-19] REGUŁY WNIOSKU O DOSTĘP DO ZASOBÓW — spółka, rodzaj, „dla kogo”.
 *
 * Polecenie właściciela, dosłownie:
 * „Tworząc «Zmiana uprawnień do dysków sieciowych» dodaj opcję podziału firmy do wyboru, ponieważ
 *  może być tak, że dysk dotyczy innej firmy — firma z definicji do wyboru, oraz dodaj opcję
 *  złożenia dysku sieciowego sharepoint, dodaj też koniecznie w polach, dla kogo ma być nadany
 *  dostęp — wyszukiwanie po pracownikach z aplikacji, jako dodanie nawet kilku osób."
 *
 * ── DLACZEGO OSOBNY MODUŁ, A NIE KOD W OKNIE ────────────────────────────────────────────────
 * `MyNewTicketModal.js` ma ~2600 linii i w tej fali pracują w nim inne obszary. Kształt
 * `AdditionalData` jest KONTRAKTEM z serwerem (`DogSoft.Application.Dyski.ParserWnioskuODostep`):
 * serwer odrzuca wniosek, w którym zasób należy do innej spółki niż wybrana, i odrzuca duplikat
 * osoby. Reguła zamknięta w renderze okna daje się zmierzyć wyłącznie przez zmontowanie całego
 * modala z ośmioma atrapami — czyli w praktyce nie daje się zmierzyć wcale.
 *
 * 🔴 ZAWĘŻENIE PO SPÓŁCE MUSI TEŻ ODSIEWAĆ JUŻ ZAZNACZONE ZASOBY. Bez tego wybór spółki byłby
 * ozdobą listy: człowiek zaznacza dysk, PÓŹNIEJ zmienia spółkę, lista się przeładowuje — a wniosek
 * i tak jedzie z zasobem innej firmy, bo `selectedDrives` trzyma same identyfikatory. Serwer
 * odrzuca to kodem `zasob_innej_spolki`, więc bez odsiania człowiek dostaje odmowę, której nie ma
 * jak zrozumieć: zasobu, o którym mówi komunikat, NIE MA JUŻ NA EKRANIE.
 */

/** Rodzaj zasobu — ta sama wartość jedzie do serwera w parametrze `kind`. */
export const RODZAJ_ZASOBU = {
    wszystkie: "",
    siec: "network",
    sharepoint: "sharepoint",
};

export const ETYKIETY_RODZAJU = {
    [RODZAJ_ZASOBU.wszystkie]: "Wszystkie zasoby",
    [RODZAJ_ZASOBU.siec]: "Dyski sieciowe",
    [RODZAJ_ZASOBU.sharepoint]: "Witryny SharePoint",
};

/** Sufit liczby osób w jednym wniosku — lustro `ParserWnioskuODostep.MaksymalnieOsob`. */
export const MAKS_OSOB_WE_WNIOSKU = 25;

/** Sufit liczby zasobów w jednym wniosku — lustro `ParserWnioskuODostep.MaksymalnieZasobow`. */
export const MAKS_ZASOBOW_WE_WNIOSKU = 50;

/** Identyfikator spółki jako LICZBA albo null. Puste pole wyboru daje "" — a `Number("")` to 0. */
export const idSpolkiLiczba = (wartosc) => {
    const n = Number(wartosc);
    return Number.isFinite(n) && n > 0 ? n : null;
};

/**
 * Zasoby, które przy wybranej spółce wolno zostawić zaznaczone.
 *
 * Semantyka `companyId` jest DOKŁADNIE ta, co na serwerze (`AssignableDrive.CompanyId`):
 * brak spółki na zasobie = zasób wspólny, widoczny przy każdym wyborze. Odwrotne znaczenie
 * („null = niczyj, odsiej") skasowałoby dziś na produkcji WSZYSTKIE zaznaczenia, bo ani jeden
 * dysk spółki nie ma.
 *
 * @param {Map<string, object>} znane   pamięć karty: id zasobu → zasób (patrz wybraneDyskiDoWniosku)
 * @param {Array<string>}       wybrane identyfikatory zaznaczonych zasobów
 * @param {number|string|null}  spolka  wybrana spółka ("" = bez zawężenia)
 * @returns {Array<string>} identyfikatory, które zostają zaznaczone
 */
export const odsiejWybraneSpozaSpolki = (znane, wybrane, spolka) => {
    const lista = Array.isArray(wybrane) ? wybrane : [];
    const id = idSpolkiLiczba(spolka);
    if (id === null) return lista;

    return lista.filter((idZasobu) => {
        // Pamięć karty jest kluczowana surowym `id` z odpowiedzi API (GUID jako napis) — sięgamy
        // najpierw po nie, a dopiero potem po wersję napisową, żeby działać też dla kluczy liczbowych.
        const zasob = znane instanceof Map
            ? (znane.get(idZasobu) ?? znane.get(String(idZasobu)))
            : null;
        // Zasobu, którego karta nie widziała, NIE odsiewamy — nie wiemy o nim nic, a odsianie
        // na podstawie niewiedzy skasowałoby zaznaczenie zrobione przed pierwszym pobraniem listy.
        if (!zasob) return true;
        const spolkaZasobu = idSpolkiLiczba(zasob.companyId ?? zasob.CompanyId);
        return spolkaZasobu === null || spolkaZasobu === id;
    });
};

/**
 * Treść pola `AdditionalData` dla zgłoszenia typu 2 — kształt OBIEKTOWY z kontraktu BH.
 *
 * Pusta lista osób znaczy „dla siebie" (tak samo rozumie to serwer) — NIE wstawiamy tu własnego
 * identyfikatora, bo wtedy wniosek złożony dla siebie wyglądałby w bazie inaczej niż każdy wniosek
 * sprzed tej fali (`DlaUserId = NULL`).
 */
export const dodatkoweDaneWnioskuOZasoby = ({ spolka, wybrane, poziomy, osoby } = {}) => ({
    CompanyId: idSpolkiLiczba(spolka),
    DlaUserIds: (Array.isArray(osoby) ? osoby : [])
        .map((o) => Number(o?.id))
        .filter((n) => Number.isFinite(n) && n > 0),
    Drives: (Array.isArray(wybrane) ? wybrane : []).map((id) => ({
        DriveId: id,
        IsReadAccessRequested: true,
        IsWriteAccessRequested: (poziomy || {})[id] === "read_write",
    })),
});

/**
 * Walidacja tego, co widać w przeglądarce. Zwraca komunikat albo null.
 * Lustro `ParserWnioskuODostep.Zwaliduj` — ale TYLKO po to, żeby człowiek zobaczył wadę zanim
 * wyśle; rozstrzyga i tak serwer (bramka w przeglądarce jest sugestią, nie zabezpieczeniem).
 */
export const bladZakresuWniosku = ({ wybrane, osoby } = {}) => {
    const zasoby = Array.isArray(wybrane) ? wybrane : [];
    const ludzie = Array.isArray(osoby) ? osoby : [];

    if (zasoby.length > MAKS_ZASOBOW_WE_WNIOSKU) {
        return `Jeden wniosek obejmuje najwyżej ${MAKS_ZASOBOW_WE_WNIOSKU} zasobów (wybrano ${zasoby.length}). Podziel go na kilka.`;
    }
    if (ludzie.length > MAKS_OSOB_WE_WNIOSKU) {
        return `Jeden wniosek obejmuje najwyżej ${MAKS_OSOB_WE_WNIOSKU} osób (wskazano ${ludzie.length}). Podziel go na kilka.`;
    }
    return null;
};

const esc = (w) => String(w ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

/**
 * Nagłówek wniosku w treści zgłoszenia: spółka, rodzaj zasobu i DLA KOGO.
 *
 * 🔴 „Dla kogo" musi stać w TREŚCI sprawy, nie tylko w tabeli wniosków. Obsługa i gospodarz zasobu
 * czytają wątek; lista osób widoczna wyłącznie w `DiskAccessRequests` znaczyłaby, że w zgłoszeniu
 * o dostęp dla trzech osób nie widać ani jednego nazwiska.
 */
export const opisZakresuWnioskuHtml = ({ nazwaSpolki, rodzaj, osoby } = {}) => {
    const ludzie = Array.isArray(osoby) ? osoby : [];
    const wiersze = [
        `<li><strong>Spółka:</strong> ${esc(nazwaSpolki) || "wszystkie (nie zawężano)"}</li>`,
        `<li><strong>Rodzaj zasobu:</strong> ${esc(ETYKIETY_RODZAJU[rodzaj] || ETYKIETY_RODZAJU[RODZAJ_ZASOBU.wszystkie])}</li>`,
        `<li><strong>Dostęp ma być nadany:</strong> ${
            ludzie.length === 0
                ? "zgłaszającemu"
                : ludzie.map((o) => `${esc(o?.nazwa)}${o?.email ? ` (${esc(o.email)})` : ""}`).join(", ")
        }</li>`,
    ];
    return `<ul>${wiersze.join("")}</ul>`;
};
